import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  adminFormDetailInputClasses,
  adminFormInputClasses,
  adminFormSelectClasses,
  adminFormTwoColGridClass,
} from '../shared/adminUx';
import {
  BRAND_ASSET_DEFAULTS,
  CONTACT_EMAIL_DEFAULTS,
  DATE_FORMAT_OPTIONS,
  DOMAINS_DEFAULTS,
  PLATFORM_IDENTITY_DEFAULTS,
  REGIONAL_DEFAULTS,
  TIME_FORMAT_OPTIONS,
  TIMEZONE_OPTIONS,
} from './settingsDefaults';
import {
  SettingsConfirmModal,
  SettingsField,
  SettingsReadOnlyRow,
  SettingsRelatedLink,
  SettingsSectionShell,
  SettingsSuccessToast,
  useSettingsPanelState,
} from './SettingsSectionShell';

const SUBSECTIONS = [
  { id: 'platform-identity', label: 'Platform Identity' },
  { id: 'brand-assets', label: 'Brand Assets' },
  { id: 'domains', label: 'Domains & URLs' },
  { id: 'contact', label: 'Contact & Email Identity' },
  { id: 'regional', label: 'Regional Defaults' },
];

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
const isHttpsUrl = (v) => /^https:\/\/.+/i.test(String(v || '').trim());

const GeneralSettingsPanel = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('platform-identity');
  const [confirm, setConfirm] = useState(null);
  const [confirmReason, setConfirmReason] = useState('');
  const fileRefs = useRef({});

  const identity = useSettingsPanelState(
    { ...PLATFORM_IDENTITY_DEFAULTS },
    { section: 'Platform Identity', sourceItemRef: 'platform_identity' }
  );
  const domains = useSettingsPanelState(
    { ...DOMAINS_DEFAULTS },
    { section: 'Domains & URLs', sourceItemRef: 'domains_urls' }
  );
  const contact = useSettingsPanelState(
    { ...CONTACT_EMAIL_DEFAULTS },
    { section: 'Contact & Email Identity', sourceItemRef: 'contact_email' }
  );
  const regional = useSettingsPanelState(
    { ...REGIONAL_DEFAULTS },
    { section: 'Regional Defaults', sourceItemRef: 'regional_defaults' }
  );
  const brand = useSettingsPanelState(
    { ...BRAND_ASSET_DEFAULTS },
    { section: 'Brand Assets', sourceItemRef: 'brand_assets' }
  );

  const panelById = {
    'platform-identity': identity,
    domains,
    contact,
    regional,
    'brand-assets': brand,
  };
  const activePanel = panelById[activeId];

  const successMessages = {
    'platform-identity': 'Platform identity updated.',
    'brand-assets': 'Brand assets updated.',
    domains: 'Domains and URLs updated.',
    contact: 'Contact and email identity updated.',
    regional: 'Regional defaults updated.',
  };

  const validateActive = (draft) => {
    const errors = {};
    if (activeId === 'platform-identity') {
      if (!draft.platformDisplayName?.trim()) {
        errors.platformDisplayName = 'Platform Display Name is required.';
      }
      if (!draft.legalEntityName?.trim()) {
        errors.legalEntityName = 'Legal Entity Name is required.';
      }
      const legalChanged =
        draft.legalEntityName !== identity.saved.legalEntityName ||
        draft.abn !== identity.saved.abn;
      if (legalChanged && !draft.abn?.trim()) {
        errors.abn = 'Enter the verified Trades Poster ABN.';
      }
    }
    if (activeId === 'domains') {
      if (!isHttpsUrl(draft.publicWebsiteUrl)) {
        errors.publicWebsiteUrl = 'Enter a valid HTTPS URL.';
      }
      if (draft.protectedAdminUrl?.trim()) {
        if (!isHttpsUrl(draft.protectedAdminUrl)) {
          errors.protectedAdminUrl = 'Enter a valid HTTPS URL.';
        } else if (/admin\.tradesposter\.com/i.test(draft.protectedAdminUrl)) {
          errors.protectedAdminUrl = 'The Admin environment must not use admin.tradesposter.com.';
        }
      }
      if (!draft.notificationSendingDomain?.trim()) {
        errors.notificationSendingDomain = 'Notification Sending Domain is required.';
      }
    }
    if (activeId === 'contact') {
      [
        ['supportEmail', 'Support email is required.'],
        ['privacyEmail', 'Privacy email is required.'],
        ['securityEmail', 'Security email is required.'],
        ['billingEmail', 'Billing email is required.'],
        ['notificationFromName', 'Notification From Name is required.'],
        ['notificationFromEmail', 'Notification From Email is required.'],
        ['notificationReplyToEmail', 'Notification Reply-To Email is required.'],
      ].forEach(([key, msg]) => {
        if (!draft[key]?.trim()) errors[key] = msg;
        else if (key.toLowerCase().includes('email') && !isValidEmail(draft[key])) {
          errors[key] = 'Enter a valid email address.';
        }
      });
      const domain = domains.saved.notificationSendingDomain;
      if (
        draft.notificationFromEmail?.trim() &&
        domain &&
        !String(draft.notificationFromEmail).endsWith(`@${domain}`)
      ) {
        errors.notificationFromEmail = 'Notification From Email must use the approved sending domain.';
      }
    }
    if (activeId === 'regional') {
      ['country', 'timezone', 'currency', 'defaultLanguage', 'dateFormat', 'timeFormat'].forEach(
        (k) => {
          if (!draft[k]) errors[k] = `${k} is required.`;
        },
      );
    }
    if (activeId === 'brand-assets') {
      if (!draft.primaryLogo) errors.primaryLogo = 'A required brand asset is missing.';
      if (!draft.compactBrandIcon) errors.compactBrandIcon = 'A required brand asset is missing.';
      if (!draft.favicon) errors.favicon = 'A required brand asset is missing.';
    }
    return errors;
  };

  const requestSave = () => {
    const errors = validateActive(activePanel.draft);
    activePanel.setErrors(errors);
    if (Object.keys(errors).length) return;

    if (activeId === 'platform-identity') {
      const legalChanged =
        activePanel.draft.legalEntityName !== activePanel.saved.legalEntityName ||
        activePanel.draft.abn !== activePanel.saved.abn;
      if (legalChanged) {
        setConfirmReason('');
        setConfirm({
          title: 'Confirm Legal Identity Change',
          message: 'This changes the legal identity information used by Trades Poster.',
          reasonRequired: true,
        });
        return;
      }
    }
    if (activeId === 'domains') {
      setConfirmReason('');
      setConfirm({
        title: 'Confirm Domain or URL Change',
        message: 'This may affect access, email delivery or platform security.',
        reasonRequired: true,
      });
      return;
    }
    if (
      activeId === 'contact' &&
      activePanel.draft.notificationFromEmail !== activePanel.saved.notificationFromEmail
    ) {
      setConfirmReason('');
      setConfirm({
        title: 'Confirm Notification From Email Change',
        message: `This changes the sender address from ${activePanel.saved.notificationFromEmail} to ${activePanel.draft.notificationFromEmail}.`,
        reasonRequired: true,
      });
      return;
    }
    performSave();
  };

  const performSave = async () => {
    const reason = confirmReason?.trim() || undefined;
    setConfirm(null);
    await activePanel.save(validateActive, successMessages[activeId], reason);
  };

  const AssetRow = ({ label, field }) => {
    const fileName = brand.draft[field];
    const savedName = brand.saved[field];
    const pendingReplace = fileName && fileName !== savedName;
    return (
      <SettingsField label={label} required error={brand.errors[field]}>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={fileName || '—'}
            readOnly
            className={`${adminFormDetailInputClasses(true)} min-w-0 flex-1`}
          />
          <input
            ref={(el) => {
              fileRefs.current[field] = el;
            }}
            type="file"
            accept="image/*,.svg,.ico"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) brand.setField(field, file.name);
            }}
          />
          <AdminHeaderAction type="button" onClick={() => fileRefs.current[field]?.click()}>
            {fileName ? 'Replace' : 'Upload'}
          </AdminHeaderAction>
          {fileName ? (
            <AdminLinkAction type="button" allowWhenRestricted onClick={() => {}}>
              Preview
            </AdminLinkAction>
          ) : null}
          {pendingReplace ? (
            <AdminFormSecondaryAction
              type="button"
              onClick={() => brand.setField(field, savedName || '')}
            >
              Cancel replacement
            </AdminFormSecondaryAction>
          ) : null}
        </div>
      </SettingsField>
    );
  };

  const body = {
    'platform-identity': (
      <>
        <div className={adminFormTwoColGridClass}>
          <SettingsField
            label="Platform Display Name"
            required
            error={identity.errors.platformDisplayName}
          >
            <input
              type="text"
              value={identity.draft.platformDisplayName}
              onChange={(e) => identity.setField('platformDisplayName', e.target.value)}
              className={`w-full ${adminFormInputClasses(false)}`}
            />
          </SettingsField>
          <SettingsField label="Legal Entity Name" required error={identity.errors.legalEntityName}>
            <input
              type="text"
              value={identity.draft.legalEntityName}
              onChange={(e) => identity.setField('legalEntityName', e.target.value)}
              className={`w-full ${adminFormInputClasses(false)}`}
            />
          </SettingsField>
          <SettingsField
            label="ABN"
            required
            setupRequired={!identity.draft.abn?.trim()}
            error={identity.errors.abn}
          >
            <input
              type="text"
              value={identity.draft.abn}
              onChange={(e) => identity.setField('abn', e.target.value)}
              className={`w-full ${adminFormInputClasses(false)}`}
              placeholder="Not configured"
            />
          </SettingsField>
        </div>
      </>
    ),
    'brand-assets': (
      <>
        <AssetRow label="Primary Logo" field="primaryLogo" />
        <AssetRow label="Compact Brand Icon" field="compactBrandIcon" />
        <AssetRow label="Favicon" field="favicon" />
        <SettingsReadOnlyRow
          label="Platform Display Name"
          value={identity.saved.platformDisplayName}
        />
      </>
    ),
    domains: (
      <>
        <div className={adminFormTwoColGridClass}>
          <SettingsField label="Public Website URL" required error={domains.errors.publicWebsiteUrl}>
            <input
              type="url"
              value={domains.draft.publicWebsiteUrl}
              onChange={(e) => domains.setField('publicWebsiteUrl', e.target.value)}
              className={`w-full ${adminFormInputClasses(false)}`}
            />
          </SettingsField>
          <SettingsField
            label="Protected Admin URL"
            required
            setupRequired={!domains.draft.protectedAdminUrl?.trim()}
            error={domains.errors.protectedAdminUrl}
          >
            <input
              type="url"
              value={domains.draft.protectedAdminUrl}
              onChange={(e) => domains.setField('protectedAdminUrl', e.target.value)}
              className={`w-full ${adminFormInputClasses(false)}`}
              placeholder="Not configured"
            />
          </SettingsField>
          <SettingsField
            label="Notification Sending Domain"
            required
            error={domains.errors.notificationSendingDomain}
          >
            <input
              type="text"
              value={domains.draft.notificationSendingDomain}
              onChange={(e) => domains.setField('notificationSendingDomain', e.target.value)}
              className={`w-full ${adminFormInputClasses(false)}`}
            />
          </SettingsField>
        </div>
        <SettingsRelatedLink onClick={() => navigate('/admin/integrations')}>
          Open Integrations
        </SettingsRelatedLink>
      </>
    ),
    contact: (
      <>
        <div className={adminFormTwoColGridClass}>
          {[
            ['supportEmail', 'Support Email'],
            ['privacyEmail', 'Privacy Email'],
            ['securityEmail', 'Security Email'],
            ['billingEmail', 'Billing Email'],
            ['notificationFromName', 'Notification From Name'],
            ['notificationFromEmail', 'Notification From Email'],
            ['notificationReplyToEmail', 'Notification Reply-To Email'],
          ].map(([key, label]) => (
            <SettingsField key={key} label={label} required error={contact.errors[key]}>
              <input
                type={key.includes('Email') ? 'email' : 'text'}
                value={contact.draft[key]}
                onChange={(e) => contact.setField(key, e.target.value)}
                className={`w-full ${adminFormInputClasses(false)}`}
              />
            </SettingsField>
          ))}
        </div>
      </>
    ),
    regional: (
      <>
        <div className={adminFormTwoColGridClass}>
          <SettingsField label="Country" required error={regional.errors.country}>
            <select
              value={regional.draft.country}
              onChange={(e) => regional.setField('country', e.target.value)}
              className={adminFormSelectClasses()}
            >
              <option value="Australia">Australia</option>
            </select>
          </SettingsField>
          <SettingsField label="Timezone" required error={regional.errors.timezone}>
            <select
              value={regional.draft.timezone}
              onChange={(e) => regional.setField('timezone', e.target.value)}
              className={adminFormSelectClasses()}
            >
              {TIMEZONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Currency" required error={regional.errors.currency}>
            <select
              value={regional.draft.currency}
              onChange={(e) => regional.setField('currency', e.target.value)}
              className={adminFormSelectClasses()}
            >
              <option value="AUD">AUD</option>
            </select>
          </SettingsField>
          <SettingsField label="Default Language" required error={regional.errors.defaultLanguage}>
            <select
              value={regional.draft.defaultLanguage}
              onChange={(e) => regional.setField('defaultLanguage', e.target.value)}
              className={adminFormSelectClasses()}
            >
              <option value="English (Australia)">English (Australia)</option>
            </select>
          </SettingsField>
          <SettingsField label="Date Format" required error={regional.errors.dateFormat}>
            <select
              value={regional.draft.dateFormat}
              onChange={(e) => regional.setField('dateFormat', e.target.value)}
              className={adminFormSelectClasses()}
            >
              {DATE_FORMAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Time Format" required error={regional.errors.timeFormat}>
            <select
              value={regional.draft.timeFormat}
              onChange={(e) => regional.setField('timeFormat', e.target.value)}
              className={adminFormSelectClasses()}
            >
              {TIME_FORMAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </SettingsField>
        </div>
      </>
    ),
  }[activeId];

  return (
    <>
      <SettingsSectionShell
        subsections={SUBSECTIONS}
        activeId={activeId}
        onSelectSubsection={setActiveId}
        hasChanges={activePanel.hasChanges}
        saving={activePanel.saving}
        onSave={requestSave}
        onCancel={activePanel.cancel}
      >
        {body}
      </SettingsSectionShell>

      {confirm ? (
        <SettingsConfirmModal
          title={confirm.title}
          message={confirm.message}
          reasonRequired={confirm.reasonRequired}
          reason={confirmReason}
          onReasonChange={setConfirmReason}
          onCancel={() => setConfirm(null)}
          onConfirm={performSave}
          confirming={activePanel.saving}
        />
      ) : null}

      <SettingsSuccessToast message={activePanel.successMessage} />
    </>
  );
};

export default GeneralSettingsPanel;
